import { FC, memo, useMemo, useState, useEffect } from 'react'
import { Button } from 'antd'
import styles from './index.module.scss'
import { useTranslation } from '@/services/i18n'
import { Web3RecordItemModel } from '@/services/apis/txRecord/models'
import { formatUnits } from 'ethers'

interface ExpandedRowProps {
  data: Web3RecordItemModel
}

const ExpandedRow: FC<ExpandedRowProps> = ({ data }) => {
  const [isVisualData, setIsVisualData] = useState(false)
  useEffect(() => {
    setIsVisualData(!!data?.transaction?.visualData)
  }, [data?.transaction?.visualData])

  const { t } = useTranslation()
  const rightColumn = useMemo(() => {
    let showRight = true
    if (data.subjectType === 'eth_signTransaction') {
      if (
        data.subjectSubType === 'contract_deploy' ||
        data.subjectSubType === 'nft_transfer' ||
        data.subjectSubType === 'token_transfer'
      ) {
        showRight = false
      }
      if (!data.transaction?.data) {
        showRight = false
      }
    }
    if (!showRight) {
      return null
    }
    let el = null
    switch (data.subjectType) {
      case 'personal_sign':
        el = (
          <div className={styles.item}>
            <span className={styles.itemLabel}>{t('txRecord.message')}:</span>
            <span className={styles.itemTextArea}>{data.message}</span>
          </div>
        )
        break
      case 'eth_signTypedData':
        el = (
          <div className={styles.item}>
            <span className={styles.itemLabel}>{t('txRecord.information')}:</span>
            <span className={styles.itemTextArea}>{data.message}</span>
          </div>
        )
        break
      case 'eth_sign':
        el = (
          <div className={styles.item}>
            <span className={styles.itemLabel}>{t('txRecord.message')}:</span>
            <span className={styles.itemValue}>{data?.messageHash?.[0]?.hash}</span>
          </div>
        )
        break
      default:
        switch (data.subjectSubType) {
          case 'native_transfer':
            el = (
              <div className={styles.item}>
                <span className={styles.itemLabel}>{t('txRecord.hexData')}:</span>
                <span className={styles.itemValue}>{data?.transaction?.data}</span>
              </div>
            )
            break
          case 'approve':
            el = (
              <div className={styles.item}>
                <span className={styles.itemLabel}>{t('txRecord.authorizedAmount')}:</span>
                <span className={styles.itemValue}>
                  {formatUnits(data.extraData.approveValue, data.decimals)}
                  {data.tokenSymbol}
                </span>
              </div>
            )
            break
          default:
            el = (
              <>
                <div className={styles.item}>
                  <span className={styles.itemLabel}>
                    {isVisualData ? t('txRecord.data') : t('txRecord.hexData')}:
                  </span>
                  {isVisualData && data?.transaction?.visualData ? (
                    <div className={styles.itemTextArea} style={{ height: 126 }}>
                      <div>Function: {data.transaction.visualData.method} </div>
                      <div>MethodID: {data.transaction.visualData.rawParameters[0]}</div>
                      {data.transaction.visualData.rawParameters.slice(1).map((item, index) => {
                        return (
                          <p>
                            <div>[{index}]:</div>
                            {item}
                          </p>
                        )
                      })}
                    </div>
                  ) : (
                    <div className={styles.itemTextArea} style={{ height: 126 }}>
                      {data?.transaction?.data}
                    </div>
                  )}
                </div>
                {data?.transaction?.visualData && (
                  <div className={styles.switchBtn}>
                    <Button
                      type={isVisualData ? 'primary' : 'default'}
                      ghost={isVisualData}
                      size="small"
                      onClick={() => setIsVisualData(true)}
                    >
                      {t('common.default')}
                    </Button>
                    <Button
                      type={!isVisualData ? 'primary' : 'default'}
                      ghost={!isVisualData}
                      size="small"
                      onClick={() => setIsVisualData(false)}
                    >
                      {t('txRecord.hex')}
                    </Button>
                  </div>
                )}
              </>
            )
            break
        }
    }
    return <div className={styles.right}>{el}</div>
  }, [data, isVisualData])
  return (
    <div className={styles.expandedRow}>
      <div className={styles.left}>
        <div className={styles.item}>
          <span className={styles.itemLabel}>{t('txRecord.from')}:</span>
          <span className={styles.itemValue}>
            {data.sourceAccountName}({data.sourceAddress})
          </span>
        </div>
        {data.subjectType === 'eth_signTransaction' && (
          <div className={styles.item}>
            <span className={styles.itemLabel}>
              {[
                'native_transfer',
                'token_transfer',
                'approve',
                'contract_unknown_invoke',
                'nft_transfer',
              ].includes(data.subjectSubType)
                ? t('txRecord.to')
                : t('txRecord.authorized')}
              :
            </span>
            <span className={styles.itemValue}>
              {data.subjectSubType === 'native_transfer'
                ? data.transaction?.to
                : data.contractAddress}
            </span>
          </div>
        )}
        {(data.subjectType === 'eth_signTypedData' ||
          data.subjectType === 'eth_signTransaction') && (
          <div className={styles.item}>
            <span className={styles.itemLabel}>{t('txRecord.network')}:</span>
            <span className={styles.itemValue}>{data.networkName}</span>
          </div>
        )}
        {data.subjectType === 'eth_signTransaction' && (
          <div className={styles.item}>
            <span className={styles.itemLabel}>{t('txRecord.signResult')} hash:</span>
            <span className={styles.itemValue}>{data.txHash}</span>
          </div>
        )}
        {data.transactionSubStatus && (
          <div className={styles.item}>
            <div className={styles.itemLabel}>{t('record.subState')}:</div>
            <div className={styles.itemValue}>{data?.transactionSubStatusDesc}</div>
          </div>
        )}
      </div>
      {rightColumn}
    </div>
  )
}

export default memo(ExpandedRow)
